import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Mail, Phone, User, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Customer auth dialog.
 * - "Create account": collects name + email + phone → backend auto-generates password,
 *   emails it via Resend, and signs the user in immediately.
 * - "Sign in": email + password.
 * Component name kept as `OtpLoginDialog` to avoid breaking imports across the app.
 */
const OtpLoginDialog = ({ open, onOpenChange }) => {
  const [tab, setTab] = useState("signup"); // signup | signin
  const [signup, setSignup] = useState({ full_name: "", email: "", phone: "", password: "", confirm_password: "" });
  const [signin, setSignin] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [signedUp, setSignedUp] = useState(null); // email of just-created user
  const { setCustomerToken } = useAuth();

  const reset = () => {
    setSignup({ full_name: "", email: "", phone: "", password: "", confirm_password: "" });
    setSignin({ email: "", password: "" });
    setSignedUp(null);
  };

  const onClose = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 250);
  };

  const handleSignup = async (e) => {
    e?.preventDefault?.();
    if (!signup.full_name || !signup.email || !signup.phone || !signup.password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (signup.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (signup.password !== signup.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: signup.full_name,
        email: signup.email,
        phone: signup.phone,
        password: signup.password,
      };
      const { data } = await api.post("/auth/customer/signup", payload);
      setCustomerToken(data.access_token, data.user);
      setSignedUp(signup.email);
      toast.success("Account ready — welcome ✦");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally { setLoading(false); }
  };

  const handleSignin = async (e) => {
    e?.preventDefault?.();
    if (!signin.email || !signin.password) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer/login", signin);
      setCustomerToken(data.access_token, data.user);
      toast.success(`Welcome back, ${data.user.full_name || "lovely"} ✦`);
      onClose(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally { setLoading(false); }
  };

  const resendPassword = async () => {
    if (!signin.email) {
      toast.error("Enter your email above first, then click resend");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/customer/resend-password", { email: signin.email });
      toast.success("Fresh password sent to your inbox");
    } catch {
      toast.error("Could not resend — please try again");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-cream border border-charcoal/10" data-testid="auth-dialog">
        {/* Top branded strip */}
        <div className="px-7 pt-7 pb-2 bg-gradient-to-br from-blush/40 via-cream to-cream">
          <DialogHeader>
            <DialogTitle className="font-hero text-3xl text-charcoal">
              {signedUp ? "Welcome aboard ✦" : tab === "signup" ? "Join the rieave family" : "Welcome back"}
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-mauve mt-1">
              {signedUp
                ? "Welcome to the family. A confirmation email is on its way."
                : tab === "signup"
                  ? "Sign up in seconds — choose a password you'll remember."
                  : "Sign in to your account to continue."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <AnimatePresence mode="wait">
          {signedUp ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="px-7 pb-7 pt-4"
              data-testid="auth-signup-success"
            >
              <div className="flex flex-col items-center text-center py-6">
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14 }}
                  className="w-16 h-16 rounded-full bg-rose/15 flex items-center justify-center mb-5"
                >
                  <CheckCircle2 size={28} className="text-rose" strokeWidth={1.5} />
                </motion.div>
                <p className="font-cormorant text-xl text-charcoal mb-2">You're all set ✦</p>
                <p className="text-sm text-mauve max-w-xs mb-6">
                  Welcome aboard, <strong className="text-charcoal">{signedUp}</strong>.
                  We've also sent a confirmation email so you have your sign-in details handy.
                </p>
                <button
                  onClick={() => onClose(false)}
                  className="btn-primary w-full"
                  data-testid="auth-success-continue"
                >
                  Start shopping →
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="px-7 pb-7 pt-4"
            >
              {/* Tabs */}
              <div className="flex gap-1 mb-6 p-1 bg-charcoal/5 rounded-full" data-testid="auth-tabs">
                {[
                  { key: "signup", label: "Create account" },
                  { key: "signin", label: "Sign in" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`flex-1 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] font-medium transition-all ${
                      tab === t.key ? "bg-cream text-charcoal shadow-sm" : "text-mauve hover:text-charcoal"
                    }`}
                    data-testid={`auth-tab-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "signup" ? (
                <form onSubmit={handleSignup} className="space-y-4" data-testid="auth-signup-form">
                  <Field icon={User} placeholder="Full name" value={signup.full_name}
                         onChange={(v) => setSignup({ ...signup, full_name: v })} testId="auth-signup-name" />
                  <Field icon={Mail} type="email" placeholder="Email address" value={signup.email}
                         onChange={(v) => setSignup({ ...signup, email: v })} testId="auth-signup-email" />
                  <Field icon={Phone} type="tel" placeholder="Phone number" value={signup.phone}
                         onChange={(v) => setSignup({ ...signup, phone: v })} testId="auth-signup-phone" />
                  <Field icon={Lock} type="password" placeholder="Create password (6+ chars)" value={signup.password}
                         onChange={(v) => setSignup({ ...signup, password: v })} testId="auth-signup-password" />
                  <Field icon={Lock} type="password" placeholder="Confirm password" value={signup.confirm_password}
                         onChange={(v) => setSignup({ ...signup, confirm_password: v })} testId="auth-signup-confirm" />
                  <button
                    type="submit" disabled={loading}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2"
                    data-testid="auth-signup-submit"
                  >
                    {loading ? "Creating account…" : <>Create my account <ArrowRight size={14} /></>}
                  </button>
                  <p className="text-[11px] text-center text-mauve mt-3 leading-relaxed">
                    By continuing you agree to our <a href="/terms" className="underline">Terms</a> and <a href="/privacy" className="underline">Privacy</a>.
                  </p>
                </form>
              ) : (
                <form onSubmit={handleSignin} className="space-y-4" data-testid="auth-signin-form">
                  <Field icon={Mail} type="email" placeholder="Email address" value={signin.email}
                         onChange={(v) => setSignin({ ...signin, email: v })} testId="auth-signin-email" />
                  <Field icon={Lock} type="password" placeholder="Password" value={signin.password}
                         onChange={(v) => setSignin({ ...signin, password: v })} testId="auth-signin-password" />
                  <button
                    type="submit" disabled={loading}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2"
                    data-testid="auth-signin-submit"
                  >
                    {loading ? "Signing in…" : <>Sign in <ArrowRight size={14} /></>}
                  </button>
                  <button
                    type="button" onClick={resendPassword}
                    className="btn-link w-full justify-center text-xs"
                    data-testid="auth-resend-password"
                  >
                    Forgot password? Email me a new one
                  </button>
                </form>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ icon: Icon, value, onChange, testId, ...rest }) => (
  <div className="relative">
    <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mauve pointer-events-none" />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pl-10 h-11 bg-white border-charcoal/10 focus:border-rose focus:ring-rose/30"
      data-testid={testId}
      {...rest}
    />
  </div>
);

export default OtpLoginDialog;
